import type { PlayerCharacter } from "../types/entity.js";

/**
 * Pure helpers for reading and mutating the per-character spell-slot
 * map (`character.spellSlots`, keyed by spell level → remaining count).
 *
 * The short-/long-rest refill paths live in `party/rest.ts`; this module
 * is the casting-side counterpart: "can I cast at level L" and "subtract
 * one slot at level L".
 *
 * Upcasting note: when a caster wants to burn a higher-level slot to
 * power a lower-level spell, `findCastableSlot(..., minLevel)` returns
 * the lowest available level ≥ minLevel — the caller then calls
 * `consumeSlot(character, foundLevel)`.
 */

/** True if the character has at least one unspent slot of exactly `level`. */
export function hasSlot(character: PlayerCharacter, level: number): boolean {
  return (character.spellSlots[level] ?? 0) > 0;
}

/**
 * Return the lowest spell-slot level ≥ `minLevel` that has an available
 * slot, or `null` if none qualifies. This is the standard upcast rule:
 * prefer cheaper slots before burning bigger ones.
 */
export function findCastableSlot(
  character: PlayerCharacter,
  minLevel: number,
): number | null {
  const levels = Object.keys(character.spellSlots)
    .map((k) => Number(k))
    .filter((n) => Number.isFinite(n) && n >= minLevel && (character.spellSlots[n] ?? 0) > 0)
    .sort((a, b) => a - b);
  return levels[0] ?? null;
}

export class NoSlotAvailableError extends Error {
  constructor(readonly level: number) {
    super(`No spell slot available at level ${String(level)}.`);
    this.name = "NoSlotAvailableError";
  }
}

/**
 * Decrement a single slot of exactly `level`. Returns a new character
 * with the updated `spellSlots` map. Throws `NoSlotAvailableError` when
 * the level has no remaining slots — callers should check `hasSlot`
 * (or `findCastableSlot`) first.
 */
export function consumeSlot(character: PlayerCharacter, level: number): PlayerCharacter {
  const remaining = character.spellSlots[level] ?? 0;
  if (remaining <= 0) throw new NoSlotAvailableError(level);
  return {
    ...character,
    spellSlots: { ...character.spellSlots, [level]: remaining - 1 },
  };
}

/**
 * Add `amount` slots back at `level`, optionally capped at `max`. Used by
 * effects that refund slots outside the rest flow (e.g. narrative rewards,
 * arcane recovery analogues). Callers that simply want the short-/long-rest
 * behaviour should stick to `party/rest.ts`.
 */
export function refundSlot(
  character: PlayerCharacter,
  level: number,
  amount = 1,
  max?: number,
): PlayerCharacter {
  if (amount <= 0) return character;
  const current = character.spellSlots[level] ?? 0;
  const next = max !== undefined ? Math.min(max, current + amount) : current + amount;
  if (next === current) return character;
  return {
    ...character,
    spellSlots: { ...character.spellSlots, [level]: next },
  };
}

/**
 * Result of the cast-side slot accounting (S03b). Whichever runtime path
 * hosts the spell-commit callback calls `beginSpellCast` before rolling
 * attacks / saves. On success the caller writes `character` back to the
 * PartyStore; on refusal it surfaces the reason as a feed line and aborts.
 *
 * The helper is the thin wrapper over `findCastableSlot` + `consumeSlot`
 * that the spell pipeline calls "from the outside". Keeping it in one
 * place means cantrips, scrolls, and reactions can all share the same
 * short-circuit (cantrips bypass via `isFreeCast` below).
 */
export type BeginSpellCastResult =
  | { readonly success: true; readonly slotLevel: number; readonly character: PlayerCharacter }
  | { readonly success: false; readonly reason: "no_slot" };

/**
 * True for spells that don't burn a slot (cantrips / level-0). Upcasting
 * from `minLevel: 0` would find the next level up, which is not what
 * cantrips want — so callers should short-circuit via this predicate.
 */
export function isFreeCast(minLevel: number): boolean {
  return minLevel <= 0;
}

/**
 * Attempt to claim a slot at level ≥ `minLevel` for `character`. Returns
 * the chosen level and the updated character on success, or `"no_slot"`
 * when the caster has nothing at that level or above.
 */
export function beginSpellCast(
  character: PlayerCharacter,
  minLevel: number,
): BeginSpellCastResult {
  const slotLevel = findCastableSlot(character, minLevel);
  if (slotLevel === null) {
    return { success: false, reason: "no_slot" };
  }
  return {
    success: true,
    slotLevel,
    character: consumeSlot(character, slotLevel),
  };
}

/**
 * Return the lowest spell-slot level with a defined max in the per-character
 * cap table, or `null` if the character has no slots configured. Used by
 * the short-rest resolver and UI hints ("short rest restores 1 level-1 slot").
 */
export function lowestSlotLevel(
  maxSlots: Readonly<Record<number, number>>,
): number | null {
  const levels = Object.keys(maxSlots)
    .map((k) => Number(k))
    .filter((n) => Number.isFinite(n) && (maxSlots[n] ?? 0) > 0)
    .sort((a, b) => a - b);
  return levels[0] ?? null;
}
